import React, { useMemo, useState } from "react";
import Table from "./TableRastreioCliente";
import TitleButtons from "../../../components/TitleButtons";
import { useListaEquipamentosReparo } from "./estoqueData";
import { useNavigate } from "react-router-dom";

function getApiBaseByHost() {
  if (typeof window === "undefined") {
    return "https://api.logvi-havox.com";
  }

  const h = window.location.hostname.toLowerCase();

  if (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h === "::1" ||
    h.includes("logvi-havox")
  ) {
    return "https://api.logvi-havox.com";
  }

  return "https://api.eva-googlemarine.com";
}

const API_BASE = getApiBaseByHost();

function safe(v: any) {
  const s = v === null || v === undefined ? "" : String(v).trim();
  return s === "" ? "N/A" : s;
}

function formatCaseRef(v: any) {
  const s = safe(v);
  if (s === "N/A") return s;

  const parts = s.split("-").filter((p) => p !== "");
  if (parts.length >= 3) return parts.slice(0, 3).join("-");
  return s;
}

function pickStatus(p: any) {
  const v =
    p?.statusAtualNomenclatura ??
    p?.status_atual_nomenclatura ??
    p?.statusAtualNome ??
    p?.status_atual_nome ??
    p?.statusAtualId ??
    p?.status_atual_id ??
    p?.status ??
    p?.prioridade;

  return safe(v);
}

function getInitials(name: string) {
  const clean = safe(name);
  if (clean === "N/A") return "?";

  const parts = clean
    .split(/\s+/)
    .map((x) => x.trim())
    .filter(Boolean);

  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();

  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function avatarBgByIndex(index: number) {
  const colors = [
    "#033B05",
    "#059669",
    "#B45309",
    "#7C3AED",
    "#BE123C",
    "#0F766E",
    "#033E2A",
    "#475569",
  ];
  return colors[index % colors.length];
}

function CopyBadges({ names }: { names: string[] }) {
  const validNames = (names ?? []).filter((n) => safe(n) !== "N/A");
  if (!validNames.length) return <span style={{ color: "#9CA3AF" }}>—</span>;

  const tooltip = validNames.join(", ");

  return (
    <div
      title={tooltip}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 0,
        minHeight: 28,
      }}
    >
      {validNames.map((name, idx) => (
        <div
          key={`${name}-${idx}`}
          title={name}
          style={{
            width: 26,
            height: 26,
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 600,
            color: "#fff",
            background: avatarBgByIndex(idx),
            border: "2px solid #fff",
            marginLeft: idx === 0 ? 0 : -6,
            boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
            userSelect: "none",
          }}
          aria-label={name}
        >
          {getInitials(name)}
        </div>
      ))}
    </div>
  );
}

const SuprimentosEstoque: React.FC = () => {
  const navigate = useNavigate();
  const { itens, loading } = useListaEquipamentosReparo();

  const headers = [
    "Case",
    "Nome do Equipamento",
    "Nome do Solicitante",
    "Em cópia",
    "Empresa do Solicitante",
    "Status",
  ];

  const columnWidths = ["160px", "320px", "240px", "150px", "320px", "220px"];

  const [selectedColumns] = useState<boolean[]>(headers.map(() => true));

  const tableItems = useMemo(() => {
    return (itens ?? []).map((p: any) => {
      const publicToken = String(
        p?.publicToken ?? p?.public_token ?? ""
      ).trim();

      const copiadosNomes =
        Array.isArray(p?.copiadosNomes) && p.copiadosNomes.length
          ? p.copiadosNomes
          : Array.isArray(p?.copiados_nomes)
          ? p.copiados_nomes
          : [];

      return {
        publicToken: publicToken || "N/A",
        caseRef: formatCaseRef(p.caseRef ?? p.case_ref),
        equipamento: safe(p.equipamentoNome ?? p.equipamento_nome),
        solicitante: safe(
          p.solicitantePrincipalNome ??
            p.solicitante_principal_nome ??
            p.solicitanteNome ??
            p.solicitante_nome
        ),
        emCopia: <CopyBadges names={copiadosNomes} />,
        empresa: safe(p.empresaRazaoSocial ?? p.empresa_razao_social),
        status: pickStatus(p),
        copiadosNomes,
      };
    });
  }, [itens]);

  const rows = useMemo(() => {
    return tableItems.map((x) => [
      x.caseRef,
      x.equipamento,
      x.solicitante,
      x.emCopia,
      x.empresa,
      x.status,
    ]);
  }, [tableItems]);

  const rowIds = useMemo(
    () => tableItems.map((x) => x.publicToken),
    [tableItems]
  );

  const caseIdx = headers.indexOf("Case");

  return (
    <div>
      <TitleButtons
        title={"Laboratório - Acompanhamento de Cases"}
        buttons={[
          {
            label: "Registrar Case",
            onClick: () =>
              navigate("/laboratorio/gestao-de-cases/registrar-case"),
            variant: "primary",
          },
        ]}
      />

      {loading ? (
        <div style={{ padding: 16 }}>Carregando…</div>
      ) : (
        <Table
          headers={headers}
          showFinanceFilter={true}
          rows={rows}
          rowIds={rowIds}
          columnWidths={columnWidths}
          companyColumnIndex={4}
          onDoubleClick={async (_viewIndex, rowData, originalIndex) => {
            const idx =
              typeof originalIndex === "number" ? originalIndex : null;

            if (idx == null || idx < 0 || idx >= tableItems.length) {
              console.warn("originalIndex inválido:", {
                originalIndex,
                rowData,
              });
              alert("Não foi possível identificar o item selecionado.");
              return;
            }

            const item = tableItems[idx];
            const publicToken = String(item?.publicToken ?? "").trim();
            const caseValue = String(rowData[caseIdx] ?? "").trim();

            if (!publicToken || publicToken === "N/A") {
              console.warn("Item sem public_token:", { item, idx, caseValue });
              alert(
                "Esse registro não possui public_token. Verifique se o endpoint retorna public_token."
              );
              return;
            }

            try {
              navigate(
                `/laboratorio/gestao-de-cases/detalhes-case/${encodeURIComponent(
                  publicToken
                )}`
              );
            } catch (e: any) {
              alert(e?.message || "Falha ao abrir detalhes do case");
            }
          }}
          responsiveWidths={{
            1500: {
              1: "150px",
              2: "300px",
              3: "220px",
              4: "140px",
              5: "280px",
              6: "220px",
            },
            1300: {
              1: "140px",
              2: "260px",
              3: "200px",
              4: "130px",
              5: "240px",
              6: "200px",
            },
          }}
        />
      )}
    </div>
  );
};

export default SuprimentosEstoque;
